// Draws a shareable concert poster on a canvas: square post (1080×1080) or Instagram story (1080×1920).
import { formatEventDate, formatTime } from "@/lib/api";

export const FORMATS = {
  square: { w: 1080, h: 1080, label: "Post carré", hint: "1080 × 1080" },
  story: { w: 1080, h: 1920, label: "Story", hint: "1080 × 1920" },
};

const ORANGE = "#f2490a";

const loadImage = (src) => new Promise((resolve) => {
  if (!src) { resolve(null); return; }
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const cover = (ctx, img, x, y, w, h) => {
  const s = Math.max(w / img.width, h / img.height);
  const iw = img.width * s;
  const ih = img.height * s;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.drawImage(img, x + (w - iw) / 2, y + (h - ih) / 2, iw, ih);
  ctx.restore();
};

const wrap = (ctx, text, maxW) => {
  const lines = [];
  let line = "";
  text.split(/\s+/).forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = word; } else line = test;
  });
  if (line) lines.push(line);
  return lines;
};

// biggest title size that fits in `maxLines`
const fitTitle = (ctx, text, maxW, maxLines, start) => {
  for (let size = start; size >= 44; size -= 4) {
    ctx.font = `900 ${size}px Montserrat`;
    const lines = wrap(ctx, text, maxW);
    if (lines.length <= maxLines && lines.every((l) => ctx.measureText(l).width <= maxW)) return { size, lines };
  }
  ctx.font = "900 44px Montserrat";
  return { size: 44, lines: wrap(ctx, text, maxW).slice(0, maxLines) };
};

export const drawPoster = async (canvas, event, kind) => {
  const { w, h } = FORMATS[kind];
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  await Promise.all(["900 80px Montserrat", "700 40px Montserrat", "600 36px 'Open Sans'"].map((f) => document.fonts?.load(f)));
  const [photo, logo] = await Promise.all([
    loadImage(event.image || "/brand/fallback.jpg").then((i) => i || loadImage("/brand/fallback.jpg")),
    loadImage("/brand/logo.png"),
  ]);

  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(0, 0, w, h);
  const imgH = kind === "story" ? 1180 : h;
  if (photo) cover(ctx, photo, 0, 0, w, imgH);
  const grad = ctx.createLinearGradient(0, imgH * (kind === "story" ? 0.55 : 0.3), 0, imgH);
  grad.addColorStop(0, "rgba(10,10,10,0)");
  grad.addColorStop(1, "rgba(10,10,10,1)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, imgH);
  ctx.fillStyle = "rgba(10,10,10,0.55)";
  ctx.fillRect(0, 0, w, 150);
  if (logo) {
    const lh = 84;
    ctx.drawImage(logo, 64, 34, (logo.width / logo.height) * lh, lh);
  }

  const pad = 72;
  const maxW = w - pad * 2;
  const { size, lines } = fitTitle(ctx, (event.title || "").toUpperCase(), maxW, kind === "story" ? 5 : 3, kind === "story" ? 120 : 104);
  const place = [event.venue, event.city].filter(Boolean).join(" · ");
  const date = `${formatEventDate(event.date)} · ${formatTime(event.date)}`.toUpperCase();
  const titleH = lines.length * size * 0.98;
  const bottom = h - (kind === "story" ? 260 : 96);
  let y = bottom - 64 - 40 - titleH - 30;

  ctx.font = "700 34px Montserrat";
  const dw = ctx.measureText(date).width + 48;
  ctx.fillStyle = ORANGE;
  ctx.fillRect(pad, y - 58, dw, 70);
  ctx.fillStyle = "#ffffff";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(date, pad + 24, y - 10);

  y += 30 + size * 0.82;
  ctx.font = `900 ${size}px Montserrat`;
  lines.forEach((l, i) => ctx.fillText(l, pad, y + i * size * 0.98));

  ctx.font = "600 40px 'Open Sans'";
  ctx.fillStyle = "#e4e4e7";
  ctx.fillText(place, pad, bottom - 20, maxW);

  ctx.fillStyle = ORANGE;
  ctx.fillRect(pad, h - (kind === "story" ? 170 : 58), 96, 8);
  ctx.font = "700 30px Montserrat";
  ctx.fillStyle = "#ffffff";
  ctx.fillText("LGTDZ.COM · RADIO, AGENDA, MAGAZINE", pad + 120, h - (kind === "story" ? 156 : 44), maxW - 120);
  return canvas;
};

export const posterBlob = (canvas) => new Promise((resolve) => canvas.toBlob(resolve, "image/png"));

// « Cette semaine sur LGTDZ » : every date of the period in one story (1080×1920). heading = { line1, line2, sub }
export const drawWeekPoster = async (canvas, events, heading) => {
  const w = 1080;
  const h = 1920;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  await Promise.all(["900 80px Montserrat", "700 40px Montserrat", "600 36px 'Open Sans'"].map((f) => document.fonts?.load(f)));
  const [photo, logo] = await Promise.all([loadImage(events[0]?.image || "/brand/fallback.jpg"), loadImage("/brand/logo.png")]);
  const pad = 72;
  const maxW = w - pad * 2;

  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(0, 0, w, h);
  if (photo) {
    ctx.globalAlpha = 0.45;
    cover(ctx, photo, 0, 0, w, 760);
    ctx.globalAlpha = 1;
  }
  const grad = ctx.createLinearGradient(0, 200, 0, 760);
  grad.addColorStop(0, "rgba(10,10,10,0)");
  grad.addColorStop(1, "rgba(10,10,10,1)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, 760);
  if (logo) ctx.drawImage(logo, pad, 70, (logo.width / logo.height) * 90, 90);

  ctx.fillStyle = "#ffffff";
  ctx.font = "900 150px Montserrat";
  ctx.fillText(heading.line1, pad, 430, maxW);
  ctx.fillStyle = ORANGE;
  ctx.fillText(heading.line2, pad, 570, maxW);
  ctx.font = "700 40px Montserrat";
  ctx.fillStyle = "#ffffff";
  ctx.fillText(heading.sub.toUpperCase(), pad, 650, maxW);

  const list = events.slice(0, 7);
  const avail = h - 240 - 730;
  const rowH = Math.min(250, Math.floor(avail / Math.max(list.length, 1)));
  const top = 730 + Math.max(0, (avail - rowH * list.length) / 2);
  const box = Math.min(170, rowH - 34);
  list.forEach((e, i) => {
    const y = top + i * rowH;
    const d = new Date(e.date);
    ctx.fillStyle = ORANGE;
    ctx.fillRect(pad, y, box, box);
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.font = `700 ${Math.round(box * 0.2)}px Montserrat`;
    ctx.fillText(d.toLocaleDateString("fr-FR", { weekday: "short" }).replace(".", "").toUpperCase(), pad + box / 2, y + box * 0.36);
    ctx.font = `900 ${Math.round(box * 0.46)}px Montserrat`;
    ctx.fillText(String(d.getDate()), pad + box / 2, y + box * 0.84);
    ctx.textAlign = "left";
    const x = pad + box + 36;
    const tw = w - pad - x;
    const size = Math.round(box * 0.3);
    ctx.font = `900 ${size}px Montserrat`;
    const maxLines = list.length <= 3 ? 3 : 2;
    const all = wrap(ctx, (e.title || "").toUpperCase(), tw);
    const lines = all.slice(0, maxLines);
    if (all.length > maxLines) lines[maxLines - 1] = `${lines[maxLines - 1].replace(/\s+\S*$/, "")}…`;
    lines.forEach((l, k) => ctx.fillText(l, x, y + size + k * size * 1.02, tw));
    ctx.font = `600 ${Math.round(box * 0.2)}px 'Open Sans'`;
    ctx.fillStyle = "#d4d4d8";
    const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    ctx.fillText([time, e.venue, e.city].filter(Boolean).join(" · "), x, y + size * (lines.length + 0.2) + box * 0.22, tw);
  });
  if (events.length > list.length) {
    ctx.font = "700 34px Montserrat";
    ctx.fillStyle = ORANGE;
    ctx.fillText(`+ ${events.length - list.length} AUTRE(S) DATE(S) SUR L'AGENDA`, pad, h - 230);
  }

  ctx.fillStyle = ORANGE;
  ctx.fillRect(pad, h - 170, 96, 8);
  ctx.font = "700 30px Montserrat";
  ctx.fillStyle = "#ffffff";
  ctx.fillText("LGTDZ.COM · RADIO, AGENDA, MAGAZINE", pad + 120, h - 156, maxW - 120);
  return canvas;
};
